import { createApi } from '@reduxjs/toolkit/query/react';

import { useUserCustomGroupsBks } from 'src/hooks/use-user-custom-groups-bks';
import {
  AssignmentPayload as AssignmentResponse,
  AssignmentsObject,
  AssignmentsWorkTypeMap,
  AssignmentUpdatePayload,
} from 'src/interfaces/assignment.types';
import authService from 'src/services/api/auth.service';
import {
  useActiveMSAssignmentsSubject,
  useActiveMSAssignmentsWorkType,
  useSelectedClassSelector,
} from 'src/store/features';
import { baseQuery, useAppDispatch } from 'src/store/redux';
import { getRelevantWorkTypeForQuery } from 'src/utils/assignments.utils';

import { AssignmentsQueryTagTypes } from './constants';
import {
  getStudentsAssignmentsQuery,
  getTeacherAssignmentsQuery,
} from './query-functions';
import {
  StudentAssignmentsQueryPayload,
  TeacherAssignmentsQueryPayload,
} from './types';
import {
  createAssignmentQueryErrorHandler,
  normalizeAssignmentsResponse,
} from './utils';

export const assignmentApi = createApi({
  reducerPath: 'assignment',
  baseQuery: baseQuery('assignment'),
  tagTypes: [
    AssignmentsQueryTagTypes.STUDENT,
    AssignmentsQueryTagTypes.TEACHER,
  ],
  endpoints: (builder) => ({
    getTeacherAssignments: builder.query<
      AssignmentsObject,
      TeacherAssignmentsQueryPayload
    >({
      query: getTeacherAssignmentsQuery,
      transformResponse: normalizeAssignmentsResponse,
      providesTags: [AssignmentsQueryTagTypes.TEACHER],
      onQueryStarted: createAssignmentQueryErrorHandler<
        AssignmentsObject,
        TeacherAssignmentsQueryPayload
      >('Something went wrong during teacher assignments fetch'),
    }),
    getK5ToDoTeacherAssignments: builder.query<
      AssignmentsObject,
      TeacherAssignmentsQueryPayload
    >({
      query: getTeacherAssignmentsQuery,
      transformResponse: normalizeAssignmentsResponse,
      providesTags: [AssignmentsQueryTagTypes.TEACHER],
      onQueryStarted: createAssignmentQueryErrorHandler<
        AssignmentsObject,
        TeacherAssignmentsQueryPayload
      >('Something went wrong during teacher assignments fetch'),
    }),
    getK5PastWorkTeacherAssignments: builder.query<
      AssignmentsObject,
      TeacherAssignmentsQueryPayload
    >({
      query: getTeacherAssignmentsQuery,
      transformResponse: normalizeAssignmentsResponse,
      providesTags: [AssignmentsQueryTagTypes.TEACHER],
      onQueryStarted: createAssignmentQueryErrorHandler<
        AssignmentsObject,
        TeacherAssignmentsQueryPayload
      >('Something went wrong during teacher assignments fetch'),
    }),
    getStudentAssignments: builder.query<
      AssignmentsObject,
      StudentAssignmentsQueryPayload
    >({
      query: getStudentsAssignmentsQuery,
      transformResponse: normalizeAssignmentsResponse,
      providesTags: [AssignmentsQueryTagTypes.STUDENT],
      onQueryStarted: createAssignmentQueryErrorHandler<
        AssignmentsObject,
        StudentAssignmentsQueryPayload
      >('Something went wrong during student assignments fetch'),
    }),
    getK5ToDoStudentAssignments: builder.query<
      AssignmentsObject,
      StudentAssignmentsQueryPayload
    >({
      query: getStudentsAssignmentsQuery,
      transformResponse: normalizeAssignmentsResponse,
      providesTags: [AssignmentsQueryTagTypes.STUDENT],
      onQueryStarted: createAssignmentQueryErrorHandler<
        AssignmentsObject,
        StudentAssignmentsQueryPayload
      >('Something went wrong during student assignments fetch'),
    }),
    getK5PastWorkStudentAssignments: builder.query<
      AssignmentsObject,
      StudentAssignmentsQueryPayload
    >({
      query: getStudentsAssignmentsQuery,
      transformResponse: normalizeAssignmentsResponse,
      providesTags: [AssignmentsQueryTagTypes.STUDENT],
      onQueryStarted: createAssignmentQueryErrorHandler<
        AssignmentsObject,
        StudentAssignmentsQueryPayload
      >('Something went wrong during student assignments fetch'),
    }),
    updateK5AssignmentStatus: builder.mutation<
      AssignmentResponse,
      AssignmentUpdatePayload
    >({
      query: (updatePayload) => ({
        url: 'status',
        method: 'POST',
        body: updatePayload,
      }),
      invalidatesTags: () => {
        const isTeacher = authService.isTeacher();

        return isTeacher
          ? [AssignmentsQueryTagTypes.TEACHER]
          : [AssignmentsQueryTagTypes.STUDENT];
      },
    }),
  }),
});

export const useStudentMSAssignmentsQueryState = () => {
  const activeMSAssignmentsWorkType = useActiveMSAssignmentsWorkType();
  const activeMSAssignmentsSubject = useActiveMSAssignmentsSubject();

  // pass correct workType instead of skipping query for Apps section - for correct realtime logic work
  const relevantWorkType = getRelevantWorkTypeForQuery(
    activeMSAssignmentsWorkType,
  );

  const studentAssignmentsQueryParams = {
    workType: relevantWorkType,
    includeSingleSessionCodes:
      activeMSAssignmentsWorkType === AssignmentsWorkTypeMap.PAST_WORK ||
      activeMSAssignmentsWorkType === AssignmentsWorkTypeMap.TODO,
  };

  return assignmentApi.endpoints.getStudentAssignments.useQueryState({
    domain: activeMSAssignmentsSubject,
    ...studentAssignmentsQueryParams,
  });
};

export const useTeacherMSAssignmentsQueryState = () => {
  const selectedClass = useSelectedClassSelector();
  const activeMSAssignmentsSubject = useActiveMSAssignmentsSubject();
  const activeMSAssignmentsWorkType = useActiveMSAssignmentsWorkType();

  // pass correct workType instead of skipping query for Apps section - for correct realtime logic work
  const relevantWorkType = getRelevantWorkTypeForQuery(
    activeMSAssignmentsWorkType,
  );

  const classBks = selectedClass ? selectedClass.map((cl) => cl.sourcedId) : [];
  const groupBks = useUserCustomGroupsBks();

  return assignmentApi.endpoints.getTeacherAssignments.useQueryState({
    classBks,
    groupBks,
    domain: activeMSAssignmentsSubject,
    workType: relevantWorkType,
    includeSingleSessionCodes:
      activeMSAssignmentsWorkType === AssignmentsWorkTypeMap.PAST_WORK,
  });
};

export const useAssignmentsRefetchWithCurrentArgs = () => {
  const isStudent = authService.isStudent();

  const dispatch = useAppDispatch();

  const assignmentQueryTagToInvalidate = isStudent
    ? AssignmentsQueryTagTypes.STUDENT
    : AssignmentsQueryTagTypes.TEACHER;

  return () => {
    dispatch(
      assignmentApi.util.invalidateTags([assignmentQueryTagToInvalidate]),
    );
  };
};

export const {
  useGetTeacherAssignmentsQuery,
  useGetStudentAssignmentsQuery,
  useGetK5ToDoStudentAssignmentsQuery,
  useGetK5PastWorkStudentAssignmentsQuery,
  useGetK5ToDoTeacherAssignmentsQuery,
  useGetK5PastWorkTeacherAssignmentsQuery,
  useUpdateK5AssignmentStatusMutation,
} = assignmentApi;
